import React from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import type { SnackbarCloseReason } from "@mui/material/Snackbar";
import { toastService, ToastType } from "./toastService";

type ToastState = {
  open: boolean;
  type: ToastType;
  message: string;
  duration: number;
};

const bgByType: Record<ToastType, string> = {
  success: "#16a34a",
  error: "#e60909",
  info: "#2563eb",
  warning: "#f59e0b",
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toast, setToast] = React.useState<ToastState>({
    open: false,
    type: "success",
    message: "",
    duration: 3000,
  });

  const close = () => setToast((p) => ({ ...p, open: false }));

  const handleClose = (_?: unknown, reason?: SnackbarCloseReason) => {
    if (reason === "clickaway") return;
    close();
  };

  const show = (type: ToastType, message: string, duration?: number) => {
    setToast({
      open: true,
      type,
      message,
      duration: duration ?? (type === "error" ? 6000 : 3000),
    });
  };

  // ✅ This MUST run, otherwise toast won't show
  React.useEffect(() => {
    toastService.register(({ type, message, duration }) => show(type, message, duration));
  }, []);

  return (
    <>
      {children}

      <Snackbar
        open={toast.open}
        autoHideDuration={toast.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 2, mr: 2 }}
      >
        <Alert
          onClose={close}
          severity={toast.type}
          sx={{
            width: "100%",
            backgroundColor: bgByType[toast.type],
            color: "#fff",
            fontWeight: 500,
            "& .MuiAlert-icon": { color: "#fff" },
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};
